<script setup>
import { computed, ref, watch } from 'vue'

import InputText from '@/components/Forms/InputText.vue'
import Combobox from '@/components/Forms/Combobox.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { defaultOrgData } from '@/stores/organizationStore.js'
import { FormData } from '@/models/enums/FormData.js'
import { OrganizationModalData } from '@/models/enums/OrganizationModalData.js'
import DatePicker from '@/components/Forms/DatePicker.vue'
import { vMaska } from 'maska/vue'
import { organizationsApi } from '@/api/Organizations/OrganizationsApi.js'
import { DatePickerObj } from '@/models/utils/InputUtils.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { ButtonSize, ButtonType, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { Switch } from '@headlessui/vue'
import { Tenants } from '@/models/tenants/Tenants.js'

const props = defineProps({
	org_id: { type: Number, default: 0 },
	show: { type: Boolean, default: false },
})

const emit = defineEmits(['evCloseOrgEditModal'])
const phone_number_unmasked = ref('')
defineExpose({ phone_number_unmasked })

//Ref values
const orgData = ref(defaultOrgData),
	isLoading = ref(true),
	error = ref(null),
	modalError = ref({ 'error_eh': false, 'error_msg': 'There was an error loading the selected organization. Please try again.' }),
	showDiscardChangesModal = ref(false),
	organization_id = ref(null),
	legal_name = ref(null),
	dba_name = ref(null),
	street = ref(null),
	city = ref(null),
	state = ref(null),
	zip = ref(null),
	phone_input = ref(null),
	facility_types = ref(['']),
	facility_type = ref(''), //Combobox: ref(null)
	npi = ref(null),
	ccn = ref(null),
	tin = ref(null),
	date_opened = ref(new DatePickerObj('', {
		dateFormat: 'Y-m-d',
		altFormat: 'm-d-Y',
		noCalendar: false,
		enableTime: false,
		time_24hr: false,
		minuteIncrement: 1,
	})),
	date_closed = ref(new DatePickerObj('', {
		dateFormat: 'Y-m-d',
		altFormat: 'm-d-Y',
		noCalendar: false,
		enableTime: false,
		time_24hr: false,
		minuteIncrement: 1,
	})),
	deleted_at = ref(null),
	organization_group = ref(''),
	organization_groups = ref([]),
	facility_ids = ref(''),
	use_dba = ref(true)

//Records the initial data so we can check for changes.
const startData = ref(null)

watch(
	() => props.show,
	(newValue) => {
		if (newValue) {
			loadModal(props.org_id)
		}
	}
)

/**
 * Shows the Modal (for the transition), sets loading flag then sends request to load the org data
 * @param p_id ORGANIZATIONS.ID tha will be shown
 */
function loadModal(p_id)
{
	orgData.value = null
	isLoading.value = true
	loadOrgData(p_id)
}

/**
 * Handles closing of dialog
 * @param p_reload_eh
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !modalError.value.error_eh && changesMade())
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

function closeModal(p_reload_eh)
{
	cleanupModal()
	emit('evCloseOrgEditModal', p_reload_eh)
}

/**
 * Tidy up
 */
function cleanupModal()
{
	resetFields()
	orgData.value = defaultOrgData
	isLoading.value = false
	error.value = null
	toggleDiscardChangesModal(false)
}

function resetFields() {
	organization_id.value = null
	legal_name.value = null
	street.value = null
	city.value = null
	state.value = null
	zip.value = ''
	phone_input.value = null
	facility_types.value = ['']
	facility_type.value = ''
	npi.value = null
	ccn.value = null
	tin.value = null
	date_opened.value = new DatePickerObj('', 'Y-m-d', 'm-d-Y')
	date_closed.value = new DatePickerObj('', 'Y-m-d', 'm-d-Y')
	deleted_at.value = null
	organization_group.value = null
	organization_groups.value = []
	facility_ids.value = null
	use_dba.value = true
}

/**
 * Loads the ORGANIZATIONS data for the set org_id prop
 * @param p_id {number} ORGANIZATIONS.ID
 */
function loadOrgData(p_id)
{
	try
	{
		if (p_id > 0)
		{
			organizationsApi.single(
				p_id,
				(p_org_data, p_facility_types) =>
				{
					isLoading.value = false
					processLoadingOrgData(p_org_data, p_facility_types)
					startData.value = orgData.value
				},
				(p_err) =>
				{
					console.log('organizationsApi.single:error', p_err)
					isLoading.value = false

					//orgStore.error = p_err
					setModalError(p_err.errorMessages())
				})
		}
		else
		{
			setModalError('')
		}
	}
	catch (e)
	{
		isLoading.value = false
		setModalError('')
		console.error('onOrganizationClicked:error', e)
	}
}

/**
 * Massages any data as necessary before displaying on the form
 * @param p_org_data {Object} Organization data for a single record
 * @param p_facility_types {Array} Distinct list of facility types Ex: [ {"facility_type": "Name"}, ...]
 * @returns {{id: number, legal_name: null, street: null, facility_type: null, city: null, state: null, zip: null, npi: null, ccn: null, tin: null, date_opened: null, date_closed: null, groups: [], use_dba: true}}
 */
function processLoadingOrgData(p_org_data, p_facility_types)
{
	loadFacilityTypes(p_facility_types);

	//Make sure fields have a value so checking for changes works and any manipulation of text
	p_org_data.id = !p_org_data.id ? 0 : p_org_data.id
	p_org_data.organization_name = !p_org_data.organization_name ? '' : p_org_data.organization_name
	p_org_data.organization_name_dba = !p_org_data.organization_name_dba ? '' : p_org_data.organization_name_dba
	p_org_data.street = !p_org_data.street ? '' : p_org_data.street
	p_org_data.city = !p_org_data.city ? '' : p_org_data.city
	p_org_data.state = !p_org_data.state ? '' : p_org_data.state
	p_org_data.zip = !p_org_data.zip ? '' : p_org_data.zip
	p_org_data.phone = !p_org_data.phone ? '' : p_org_data.phone
	p_org_data.facility_type = !p_org_data.facility_type ? '' : p_org_data.facility_type
	p_org_data.npi = !p_org_data.npi ? '' : p_org_data.npi
	p_org_data.ccn = !p_org_data.ccn ? '' : p_org_data.ccn
	p_org_data.tin = !p_org_data.tin ? '' : p_org_data.tin
	p_org_data.groups = !p_org_data.groups ? [] : p_org_data.groups

	let cleaned_phone = p_org_data.phone.replace(/\s*-*\(*\)*/g, '')
	organization_id.value = p_org_data.id
	legal_name.value = p_org_data.organization_name
	dba_name.value = p_org_data.organization_name_dba
	street.value = p_org_data.street
	city.value = p_org_data.city
	state.value = p_org_data.state
	zip.value = p_org_data.zip
	phone_input.value = cleaned_phone
	facility_type.value = p_org_data.facility_type
	npi.value = p_org_data.npi
	ccn.value = p_org_data.ccn
	tin.value = p_org_data.tin
	date_opened.value = new DatePickerObj(p_org_data.date_opened, 'Y-m-d', 'm-d-Y')
	date_closed.value = new DatePickerObj(p_org_data.date_closed, 'Y-m-d', 'm-d-Y')
	deleted_at.value = p_org_data.deleted_at

	organization_groups.value = p_org_data.groups
	organization_group.value = p_org_data.groups.map((group) => group.name).join(', ')

	facility_ids.value = p_org_data.facility_ids.join(', ')

	use_dba.value = p_org_data.use_dba === 1

	orgData.value = p_org_data
}

/**
 * Creates an array of facility types for the combobox
 * @param p_facility_types {Array} Distinct list of facility types Ex: [ {"facility_type": "Name"}, ...]
 */
function loadFacilityTypes(p_facility_types)
{
	try
	{
		facility_types.value = [''];
		for (let i = 0; i < p_facility_types.length; i++)
		{
			//Don't add nulls, it'll break the sorting
			if (p_facility_types[i].facility_type)	facility_types.value.push(p_facility_types[i].facility_type);
		}
		facility_types.value.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
	}
	catch(e)
	{
		console.log('Failed to load facility types', e);
	}
}

/**
 * Save the organization data
 */
function saveOrg()
{
	const organization = {
		'id': organization_id.value,
		'organization_name': legal_name.value,
		'organization_name_dba': dba_name.value,
		'street': street.value,
		'city': city.value,
		'state': state.value,
		'zip': zip.value.toString(),
		'phone': phone_number_unmasked.value,
		'facility_type': facility_type.value,
		'npi': npi.value,
		'ccn': ccn.value,
		'tin': tin.value,
		'date_opened': date_opened.value.getDate(),
		'date_closed': date_closed.value.getDate(),
		'use_dba': use_dba.value,
		'facility_ids': facility_ids.value,
	}

	isLoading.value = true

	organizationsApi.update({ organization: organization },
		() =>
		{//p_response = incoming response
			isLoading.value = false
			closeModal(true)
		},
		(p_err) =>
		{
			isLoading.value = false
			error.value = p_err.showErrorBanner()
		}
	)
}

/**
 * Checks if any data has been modified when saving
 * @returns {boolean} True if changes, false if values unchanged
 */
function changesMade()
{
	return checkForChange(startData.value.organization_name, legal_name.value) ||
		checkForChange(startData.value.organization_name_dba, dba_name.value) ||
		checkForChange(startData.value.facility_type, facility_type.value) ||
		checkForChange(startData.value.street, street.value) ||
		checkForChange(startData.value.city, city.value) ||
		checkForChange(startData.value.state, state.value) ||
		checkForChange(startData.value.zip, zip.value) ||
		checkForChange(startData.value.phone, phone_number_unmasked.value) ||
		checkForChange(startData.value.npi, npi.value) ||
		checkForChange(startData.value.ccn, ccn.value) ||
		checkForChange(startData.value.tin, tin.value) ||
		checkForChange(startData.value.date_opened, date_opened.value.getDate()) ||
		checkForChange(startData.value.date_closed, date_closed.value.getDate()) ||
		checkForChange(startData.value.use_dba, Number(use_dba.value))
}

function checkForChange(p_start, p_now)
{
	if (p_start !== p_now)
	{
		return !(p_start === null && p_now === '' || p_start === '' && p_now === null);
	}

	return false
}

/**
 * Show generic error if something occurs like error getting record data
 * @param {string | string[]} p_err_msg
 */
function setModalError(p_err_msg)
{
	let err_message = ''
	if (!p_err_msg || p_err_msg.length === 0)
	{
		err_message = 'There was an error loading the selected organization. Please try again.'
	}

	if (Array.isArray(p_err_msg))
	{
		err_message = p_err_msg.join('<br/>')
	}

	modalError.value.error_eh = true
	modalError.value.error_msg = err_message

	orgData.value = defaultOrgData
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Checks if a menu item is disabled for a tenant.
 * @param {string} p_route_name Route name
 * @returns {boolean} True if route is not disabled for tenant
 */
function canViewMenu(p_route_name)
{
	return Tenants.isModuleEnabled(p_route_name)
}

const facilityIdsEditModalConfig = computed(() => {
	const disabled = deleted_at.value !== null || isLoading.value
	return {
		title: 'Facility IDs',
		message: disabled ? '' : 'Enter any number of Facility IDs, each separated by a comma.',
		text: facility_ids.value,
		buttonSize: ButtonSize.Small,
		buttonWidth: ButtonWidth.Full,
		buttonText: disabled ? 'Close' : 'Save',
		placeholder: '0123456789, 0987654321',
		disabled: disabled,
		onButtonClicked: (text) => { saveFacilityIds(text) },
		inputFilter: filterInput,
	}
})

const filterInput = (event) => {
	event.target.value = event.target.value.replace(/[^a-zA-Z0-9,]/g, '')
}

function saveFacilityIds(text) {
	// Trim leading and trailing commas, then make the string prettier
	facility_ids.value = text.replace(/^,+|,+$/g, '').replace(/,+/g, ', ')
}

</script>

<template>
	<div>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div class="w-[692px]">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Edit Organization</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="modalError.error_eh">
					<div class="w-130 mt-8">{{ modalError.error_msg }}</div>
				</div>

				<form v-if="!modalError.error_eh">
					<!-- Prevent implicit submission of the form -->
					<button type="submit" disabled style="display: none" aria-hidden="true"></button>

					<div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 w-full pt-6">
						<!-- Legal name / DBA name -->
						<InputText label="Legal name*" type="text" placeholder="Legal name" input-height="h-10" autocomplete="off"
											 v-model="legal_name" :errors="error" error-field="organization.organization_name" icon="edit" :disabled="deleted_at !== null || isLoading" />
						<div class="relative">
							<Popper class="absolute right-0" :content="use_dba ? OrganizationModalData.USE_DBA_ON_TOOLTIP : OrganizationModalData.USE_DBA_OFF_TOOLTIP" hover arrow placement="bottom-start">
								<div class="flex space-x-2 items-center">
										<span class="text-label cursor-default">Use DBA</span>
										<Switch
											v-model="use_dba"
											:class="deleted_at ? 'bg-disabled' : use_dba ? 'bg-primary' : 'bg-primary-darker'"
											:inert="deleted_at"
											class="inline-flex h-4 w-9 m-1 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
										>
											<span class="sr-only">Use DBA</span>
											<span
												aria-hidden="true"
												:class="deleted_at ? 'border-disabled' : use_dba ? 'translate-x-4 border border-primary' : 'translate-x-0 border border-primary-darker'"
												class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out"
											/>
										</Switch>
								</div>
							</Popper>
							<InputText label="DBA name*" type="text" placeholder="DBA name" input-height="h-10" autocomplete="off"
												 v-model="dba_name" :errors="error" error-field="organization.organization_name_dba" icon="edit" :disabled="deleted_at !== null || isLoading" />
						</div>

						<!-- Address / City -->
						<InputText label="Address*" type="text" placeholder="Physical address" input-height="h-10" autocomplete="off"
											 v-model="street" :errors="error" error-field="organization.street" icon="edit" :disabled="deleted_at !== null || isLoading" />
						<InputText label="City*" type="text" placeholder="Enter city" v-model="city" autocomplete="off"
											 input-height="h-10" :errors="error" error-field="organization.city" icon="edit" :disabled="deleted_at !== null || isLoading" />

						<!-- State / ZIP -->
						<Combobox id="combo_states" v-model="state" :items="FormData.STATES" title="State*" placeholder="Select state"
											:show-options-on-top="true" input-height="h-10" :errors="error" error-field="organization.state" :readonly="false"
											icon="edit" :disabled="deleted_at !== null || isLoading" />
						<InputText label="ZIP code*" type="number" placeholder="Enter 5 digit code" :max-length=5 input-height="h-10" autocomplete="off"
											 v-model="zip" :errors="error" error-field="organization.zip" icon="edit" :disabled="deleted_at !== null || isLoading" />

						<!-- Phone / CCN -->
						<InputText id="org_edit_phone" label="Phone" type="tel" placeholder="Enter Phone number" input-height="h-10"
											 autocomplete="off" v-model="phone_input" :errors="error" error-field="organization.phone"
											 v-maska:phone_number_unmasked.unmasked="'(###) ###-####'" icon="edit" :disabled="deleted_at !== null || isLoading" />
						<InputText label="CCN" type="text" placeholder="Enter CCN" v-model="ccn" autocomplete="off"
								   input-height="h-10" :errors="error" error-field="organization.ccn" :tooltip="OrganizationModalData.CCN_TOOLTIP"
								   tooltip-location="bottom" icon="edit" :disabled="deleted_at !== null || isLoading" />

						<!-- TIN / NPI -->
						<InputText label="TIN" type="text" placeholder="Enter TIN" v-model="tin" autocomplete="off"
								   input-height="h-10" :errors="error" error-field="organization.tin" :tooltip="OrganizationModalData.TIN_TOOLTIP"
								   tooltip-location="bottom" icon="edit" :disabled="deleted_at !== null || isLoading" />
						<InputText label="NPI" type="text" placeholder="Enter NPI" v-model="npi" autocomplete="off"
											 input-height="h-10" :errors="error" error-field="organization.npi" :tooltip="OrganizationModalData.NPI_TOOLTIP"
											 tooltip-location="bottom" icon="edit" :disabled="deleted_at !== null || isLoading" />
						<!-- Facility ID / Facility Type -->
						<InputText label="Facility IDs" type="text" placeholder="Enter Facility IDs" v-model="facility_ids" autocomplete="off" input-height="h-10"
								   :disabled="deleted_at !== null || isLoading" :errors="error" error-field="organization.facility_ids"
								   :use-edit-modal="true" :edit-modal-config="facilityIdsEditModalConfig" />
						<Combobox id="combo_facility_type" v-model="facility_type" :items="facility_types" title="Facility type"
								  placeholder="Select" icon="edit" input-height="h-10" :errors="error"
								  error-field="organization.facility_type" :readonly="false" :enterable="true" :disabled="deleted_at !== null || isLoading" />
						<!-- TIN / Open (model must be DatePickerObj -->

						<DatePicker id="date_opened" label="Open date" placeholder="Open date"
												:model-value="date_opened" :errors="error" error-field="organization.date_opened"
												class="flex-grow " custom-classes="placeholder-default" :disabled="deleted_at !== null || isLoading" />
						<!-- Organization Groups -->
						<InputText label="Organization groups" type="text" placeholder="" v-model="organization_group" input-height="h-10" :disabled="true" v-if="canViewMenu('admin.organization-groups')" />
						<!-- Closed (model must be DatePickerObj / Groups -->
						<DatePicker id="date_closed" label="Close date" placeholder="Close date"
												:model-value="date_closed" :errors="error" error-field="organization.date_closed"
												class="flex-grow" custom-classes="placeholder-default" :disabled="deleted_at !== null || isLoading" />

					</div>

					<div class="border-b border-neutral w-full h-[1px] mt-1 mb-5"></div>

					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div>
						<SubmitButton :label="deleted_at == null ? 'Cancel' : 'Close'" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
													class="mt-3 sm:mt-0" :class="{'sm:mr-4': deleted_at == null}" :disabled="isLoading" />
						<SubmitButton label="Save" v-if="deleted_at == null" :size="ButtonSize.Small" :loading="isLoading" @click.prevent="saveOrg" :disabled="deleted_at !== null || isLoading" />

					</div>

					<ValidationError :errors="error" :field="null" />
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="alert"
				title="Discard Changes"
				message="Are you sure? Any unsaved changes will be lost."
				:buttons="[
									new ModalButtonData(
										'Cancel', () => { toggleDiscardChangesModal(false) }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { closeModal(false) }, ButtonType.Default, ButtonSize.Small
									)
								]"
			/>
		</Teleport>
	</div>
</template>